import _ from 'lodash';
import moment from 'moment';

import env from '@/config/env';
import { CoreEnums } from '@/utils/domain/constants';
import { isJsonLike, isNullOrEmpty, parseCurrencyToNumber } from '@/utils/infrastructure';
import CoreFormula from './formula';

const { FormFieldType, FormFieldStringType, MathOperationType, ComparisonOperator } = CoreEnums;
const { ConvertToNumber, applyNumberFormat } = CoreFormula;

function checkGroupedFilters(options = {}, groupedFilters = []) {
  return _.some(groupedFilters, (o) => {
    const tableFieldName = o?.sourceField?.internalName || options?.sourceAutoCalculatedTableField;
    const sourceFieldValue = options?.record?.[tableFieldName];

    const comparisonValue = o?.comparisonValue ?? o?.fixedValue;
    const value = sourceFieldValue?.value ?? sourceFieldValue;

    switch (o.comparisonOperator) {
      case ComparisonOperator.Contains:
        return value?.includes?.(comparisonValue);

      case ComparisonOperator.Equal:
        return value === comparisonValue;

      case ComparisonOperator.NotEqual:
        return value !== comparisonValue;

      case ComparisonOperator.GreaterThan:
        return value > comparisonValue;

      case ComparisonOperator.GreaterThanOrEqual:
        return value >= comparisonValue;

      case ComparisonOperator.LessThan:
        return value < comparisonValue;

      case ComparisonOperator.LessThanOrEqual:
        return value <= comparisonValue;

      case ComparisonOperator.NotContains:
        return !value?.includes?.(comparisonValue);

      case ComparisonOperator.IsNull:
        return isNullOrEmpty(value);

      case ComparisonOperator.IsNotNull:
        return !isNullOrEmpty(value);

      default:
        return;
    }
  });
}

export function checkTableFieldFilters(options = {}) {
  const groupedBy = _.chain(options?.fieldFilters)
    .filter((s) => (s.sourceField ? s.sourceForm?.id == options?.formId : true))
    .groupBy((s) => s.sourceField?.id)
    .value();

  return _.every(groupedBy, (groupedFilters) => checkGroupedFilters(options, groupedFilters));
}

export function autoCalculateByTable(dtt, props) {
  let total = null;
  const type = props.mathOperation ?? MathOperationType.Multiplication;

  switch (type) {
    case MathOperationType.Concatenate:
      total = '';
      for (let i = 0; i < dtt.length; i++) {
        const dt = dtt[i];
        let value = dt[props.sourceAutoCalculatedTableField];

        if (isJsonLike(value)) {
          const obj = JSON.parse(value);
          value = obj.items ? _.head(obj.items)?.text : obj.text;
        } else if (moment.isMoment(value)) {
          const format =
            dt[`${props.sourceAutoCalculatedTableField}_format`] === FormFieldType.Date ||
            dt[`${props.sourceAutoCalculatedTableField}_format`] === FormFieldStringType.Date
              ? env.DATE_PARSE_FORMAT
              : env.DATE_TIME_PARSE_FORMAT;

          value = value.format(format);
        } else if (_.isObject(value) && !_.isArray(value)) {
          const item = value.items ? _.head(value.items) : value;
          value = item?.text || item?.label;
        }

        if (i > 0) total += props.separator || '';
        total += value;
      }
      break;

    case MathOperationType.Count:
      total = dtt.length;
      if (isNullOrEmpty(total) || _.isNaN(total)) total = 0;
      if (!isNullOrEmpty(props.format)) {
        const number = CoreFormula.extractNumber(total);
        if (props.isNumeric || props.format) {
          props.form?.setFieldValue(`${props.name}_raw_value`, number);
        }
        total = number; // applyNumberFormat(number, props.format);
      }
      break;

    default:
      for (let i = 0; i < dtt.length; i++) {
        const dt = dtt[i];
        try {
          let value = dt[props.sourceAutoCalculatedTableField];

          if (isNullOrEmpty(value)) value = 0;
          if (_.isString(value)) value = CoreFormula.extractNumber(value);

          switch (type) {
            case MathOperationType.Multiplication:
              if (total === null) total = ConvertToNumber(value);
              else total *= ConvertToNumber(value);
              break;

            case MathOperationType.Division:
              if (total === null) total = ConvertToNumber(value);
              else {
                if (ConvertToNumber(value) === 0) {
                  total = 0;
                  i = dtt.length + 1;
                } else total /= ConvertToNumber(value);
              }
              break;

            case MathOperationType.Sum:
            case MathOperationType.Average:
              if (total === null) total = ConvertToNumber(value);
              else total += ConvertToNumber(value);
              break;

            case MathOperationType.Subtraction:
              if (total === null) total = ConvertToNumber(value);
              else total -= ConvertToNumber(value);
              break;

            case MathOperationType.Max:
              if (total === null) total = ConvertToNumber(value);
              else {
                var newN = ConvertToNumber(value);
                if (newN > total) total = newN;
              }
              break;

            case MathOperationType.Min:
              if (total === null) total = ConvertToNumber(value);
              else {
                var newN = ConvertToNumber(value);
                if (newN < total) total = newN;
              }
              break;
          }
        } catch (e) {}
      }

      if (total === null || _.isNaN(total)) total = 0;
      else {
        if (props.mathOperation === MathOperationType.Average) {
          if (total > 0 && dtt.length > 0) {
            total = total / dtt.length;
          }
        }
      }

      if (isNullOrEmpty(total) || _.isNaN(total)) total = 0;

      if (isNullOrEmpty(props.format)) {
        total = total.toFixed(props.maxDecimalPlaces ?? 2);
      } else {
        const number = CoreFormula.extractNumber(total);
        if (props.isNumeric || props.format) {
          props.form?.setFieldValue(`${props.name}_raw_value`, number);
        }
        total = number; //applyNumberFormat(number, props.format);
      }

      break;
  }

  return total;
}
